import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Clock, FileText, User, Truck } from 'lucide-react';
import SearchAutocomplete from './SearchAutocomplete';
import RecentSearches from './RecentSearches';
import SearchResults from './SearchResults';

const GlobalSearch = ({ 
  onNavigate,
  drivers = [],
  vehicles = [],
  documents = [],
  placeholder = "Search drivers, vehicles, documents...",
  maxResults = 50,
  className = ""
}) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isSearching, setIsSearching] = useState(false);
  
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const saved = localStorage.getItem('globalSearchHistory');
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved));
      } catch (e) {
        console.warn('Failed to load search history');
      }
    }
  }, []);

  useEffect(() => {
    if (query.trim()) {
      performSearch(query);
    } else {
      setSearchResults([]);
    }
  }, [query, drivers, vehicles, documents]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current && 
        !searchRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const performSearch = async (searchQuery) => {
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    
    // Simulate async search delay
    await new Promise(resolve => setTimeout(resolve, 100));
    
    const results = [];
    const lowerQuery = searchQuery.toLowerCase();

    // Search drivers
    drivers.forEach(driver => {
      const score = getSearchScore(driver, lowerQuery, 'driver');
      if (score > 0) {
        results.push({
          id: `driver-${driver.id}`,
          type: 'driver',
          title: driver.name || `${driver.firstName} ${driver.lastName}`,
          subtitle: `License: ${driver.licenseStatus || 'N/A'} | HOS: ${driver.hosStatus || 'N/A'}`,
          data: driver,
          score,
          icon: User
        });
      }
    });

    // Search vehicles
    vehicles.forEach(vehicle => {
      const score = getSearchScore(vehicle, lowerQuery, 'vehicle');
      if (score > 0) {
        results.push({
          id: `vehicle-${vehicle.id}`,
          type: 'vehicle',
          title: vehicle.vehicleNumber || `${vehicle.make} ${vehicle.model}`,
          subtitle: `${vehicle.year} ${vehicle.make} ${vehicle.model} | Status: ${vehicle.status}`,
          data: vehicle,
          score,
          icon: Truck
        });
      }
    });

    // Search documents
    documents.forEach((doc, index) => {
      const score = getSearchScore(doc, lowerQuery, 'document');
      if (score > 0) {
        results.push({
          id: `document-${index}`,
          type: 'document',
          title: doc.name || doc.title || 'Unnamed Document',
          subtitle: `Type: ${doc.type || 'Document'} | ${doc.driverName ? `Driver: ${doc.driverName}` : ''}`,
          data: doc,
          score,
          icon: FileText
        });
      }
    });

    // Sort by relevance score and limit results
    const sortedResults = results
      .sort((a, b) => b.score - a.score)
      .slice(0, maxResults);

    setSearchResults(sortedResults);
    setIsSearching(false);
  };

  const getSearchScore = (item, query, type) => {
    let score = 0;
    
    const searchFields = {
      driver: ['name', 'firstName', 'lastName', 'licenseStatus', 'hosStatus'],
      vehicle: ['vehicleNumber', 'make', 'model', 'licensePlate', 'status', 'assignedDriver'],
      document: ['name', 'title', 'type', 'driverName']
    };

    const fields = searchFields[type] || [];
    
    fields.forEach(field => {
      const value = item[field];
      if (value && typeof value === 'string') {
        const lowerValue = value.toLowerCase();
        if (lowerValue.includes(query)) {
          // Exact match gets higher score
          if (lowerValue === query) score += 100;
          // Start of word match gets medium score
          else if (lowerValue.startsWith(query)) score += 50;
          // Contains match gets base score
          else score += 25;
        }
      }
    });

    return score;
  };

  const saveToHistory = (searchTerm) => {
    if (!searchTerm.trim()) return;
    
    const newHistory = [
      { query: searchTerm, timestamp: Date.now() },
      ...recentSearches.filter(item => item.query !== searchTerm)
    ].slice(0, 10);
    
    setRecentSearches(newHistory);
    localStorage.setItem('globalSearchHistory', JSON.stringify(newHistory));
  };

  const handleInputChange = (value) => {
    setQuery(value);
    setSelectedIndex(-1);
    if (!isOpen) setIsOpen(true);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleSearch = (searchTerm = query) => {
    if (!searchTerm.trim()) return;
    
    saveToHistory(searchTerm);
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e) => {
    const totalItems = (query.trim() ? searchResults.length : recentSearches.length);
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => prev < totalItems - 1 ? prev + 1 : -1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > -1 ? prev - 1 : totalItems - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          if (query.trim()) {
            handleResultSelect(searchResults[selectedIndex]);
          } else {
            handleRecentSearchSelect(recentSearches[selectedIndex]);
          }
        } else {
          handleSearch();
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleResultSelect = (result) => {
    if (onNavigate) {
      onNavigate(result.type, result.data);
    }
    saveToHistory(query);
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  const handleRecentSearchSelect = (recentSearch) => {
    setQuery(recentSearch.query);
    handleSearch(recentSearch.query);
  };

  const clearSearch = () => {
    setQuery('');
    setSearchResults([]);
    setSelectedIndex(-1);
    searchRef.current?.focus();
  };

  const clearHistory = () => {
    setRecentSearches([]);
    localStorage.removeItem('globalSearchHistory');
  };

  return (
    <div className={`relative ${className}`} ref={searchRef}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        
        <input
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        />
        
        {query && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <button
              onClick={clearSearch}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-4 w-4 text-gray-400" />
            </button>
          </div>
        )}
      </div>

      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-96 overflow-hidden"
        >
          {query.trim() ? (
            <SearchResults
              results={searchResults}
              selectedIndex={selectedIndex}
              isSearching={isSearching}
              onResultSelect={handleResultSelect}
              query={query}
            />
          ) : (
            <RecentSearches
              recentSearches={recentSearches}
              selectedIndex={selectedIndex}
              onSearchSelect={handleRecentSearchSelect}
              onClearHistory={clearHistory}
            />
          )}
        </div>
      )}

      <SearchAutocomplete
        query={query}
        searchResults={searchResults}
        onSuggestionSelect={handleInputChange}
      />
    </div>
  );
};

export default GlobalSearch;